import { useState, useEffect, useMemo } from 'react';
import { Card, Button, MultiSelect, Select } from "@repo/o37-ui";
import { FundraisingActivity, FundraisingActivityType, FundraisingFund } from '../../types';
import { Search, Filter, Plus, Calendar, User, Phone, Mail, FileText, Mic, CheckCircle2, Clock, Building2 } from "lucide-react";
import { Link } from 'react-router-dom';

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

interface RaiseActivitiesProps {
  raiseId: string;
  funds: FundraisingFund[];
}

const ACTIVITY_TYPES: { type: FundraisingActivityType; label: string; icon: any; color: string }[] = [
  { type: 'note', label: 'Note', icon: FileText, color: 'text-neutral-500 bg-neutral-100' },
  { type: 'email', label: 'Email', icon: Mail, color: 'text-blue-500 bg-blue-100' },
  { type: 'call', label: 'Call', icon: Phone, color: 'text-purple-500 bg-purple-100' },
  { type: 'meeting', label: 'Meeting', icon: Calendar, color: 'text-amber-500 bg-amber-100' },
  { type: 'pitch', label: 'Pitch', icon: Mic, color: 'text-rose-500 bg-rose-100' },
  { type: 'stage_change', label: 'Stage Change', icon: CheckCircle2, color: 'text-emerald-500 bg-emerald-100' },
];

export default function RaiseActivities({ raiseId, funds }: RaiseActivitiesProps) {
  const [activities, setActivities] = useState<(FundraisingActivity & { fund?: { id: string, name: string } })[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<FundraisingActivityType | 'all'>('all');
  const [fundFilter, setFundFilter] = useState<string>('all');

  // New Activity State
  const [isAdding, setIsAdding] = useState(false);
  const [selectedFundId, setSelectedFundId] = useState<string>('');
  const [newActivityType, setNewActivityType] = useState<FundraisingActivityType>('note');
  const [newContent, setNewContent] = useState('');
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const userId = localStorage.getItem('app_user_id') || '';
  const userRole = localStorage.getItem('app_role') || 'user';

  useEffect(() => {
    loadActivities();
  }, [raiseId]);

  const loadActivities = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/fundraising/raises/${raiseId}/activities`, {
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });
      if (response.ok) {
        const data = await response.json();
        setActivities(data.map((a: any) => ({
          id: a.id,
          fundId: a.fund_id,
          contactId: a.contact_id,
          contact: a.contact,
          contacts: a.activity_contacts?.map((ac: any) => ac.contact).filter(Boolean),
          type: a.type,
          content: a.content,
          metadata: a.metadata,
          createdBy: a.created_by,
          createdAt: a.created_at,
          fund: a.fund
        })));
      }
    } catch (e) {
      console.error('Failed to load activities:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleAddActivity = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newContent.trim() || !selectedFundId) return;

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/fundraising/funds/${selectedFundId}/activities`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
          'x-user-role': userRole
        },
        body: JSON.stringify({
          type: newActivityType,
          content: newContent.trim(),
          contactIds: selectedContactIds
        })
      });

      if (response.ok) {
        setNewContent('');
        setSelectedContactIds([]);
        setSelectedFundId('');
        setIsAdding(false);
        loadActivities();
      }
    } catch (e) {
      console.error('Failed to add activity:', e);
    } finally {
      setSubmitting(false);
    }
  };

  const filteredActivities = useMemo(() => {
    return activities
      .filter(a => {
        const contactNames = a.contacts?.map(c => c.name).join(' ') || a.contact?.name || '';
        const matchesSearch = a.content.toLowerCase().includes(searchQuery.toLowerCase()) || 
                              contactNames.toLowerCase().includes(searchQuery.toLowerCase()) ||
                              a.fund?.name.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesType = typeFilter === 'all' || a.type === typeFilter;
        const matchesFund = fundFilter === 'all' || a.fundId === fundFilter;
        return matchesSearch && matchesType && matchesFund;
      })
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [activities, searchQuery, typeFilter, fundFilter]);

  // Group by date
  const groupedActivities = useMemo(() => {
    const groups: Record<string, typeof activities> = {};
    filteredActivities.forEach(activity => {
      const date = new Date(activity.createdAt).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
      if (!groups[date]) groups[date] = [];
      groups[date].push(activity);
    });
    return groups;
  }, [filteredActivities]);

  const formatTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getTypeConfig = (type: string) => {
    return ACTIVITY_TYPES.find(t => t.type === type) || 
           { type: 'note', label: 'Activity', icon: FileText, color: 'text-neutral-500 bg-neutral-100' };
  };

  // Get contacts for selected fund
  const selectedFundContacts = useMemo(() => {
    if (!selectedFundId) return [];
    const fund = funds.find(f => f.id === selectedFundId);
    return fund?.contacts || [];
  }, [selectedFundId, funds]);

  return (
    <div className="space-y-6">
      {/* Controls Header */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-2 w-full sm:w-auto flex-1 max-w-2xl">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <input
              type="text"
              placeholder="Search activities..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-200 transition-all"
            />
          </div>
          <div className="relative w-40">
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as any)}
              className="w-full pl-3 pr-8 py-2 text-sm border border-neutral-200 rounded-lg appearance-none bg-white focus:outline-none focus:ring-2 focus:ring-neutral-200 cursor-pointer"
            >
              <option value="all">All Types</option>
              {ACTIVITY_TYPES.map(t => (
                <option key={t.type} value={t.type}>{t.label}</option>
              ))}
            </select>
            <Filter className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400 pointer-events-none" />
          </div>
          <div className="relative w-48">
            <select
              value={fundFilter}
              onChange={(e) => setFundFilter(e.target.value)}
              className="w-full pl-3 pr-8 py-2 text-sm border border-neutral-200 rounded-lg appearance-none bg-white focus:outline-none focus:ring-2 focus:ring-neutral-200 cursor-pointer"
            >
              <option value="all">All Funds</option>
              {funds.map(f => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
            <Building2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400 pointer-events-none" />
          </div>
        </div>
        
        <Button
          onClick={() => setIsAdding(!isAdding)}
          icon={<Plus className="w-4 h-4" />}
          text="Log Activity"
          variant={isAdding ? "secondary" : "primary"}
        />
      </div>

      {/* Add Activity Form */}
      {isAdding && (
        <Card className="animate-in slide-in-from-top-2 border-neutral-200 shadow-sm overflow-hidden">
          <div className="p-4 bg-neutral-50/50 border-b border-neutral-100 flex flex-col gap-4">
            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              {ACTIVITY_TYPES.filter(t => t.type !== 'stage_change').map(t => (
                <button
                  key={t.type}
                  onClick={() => setNewActivityType(t.type)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
                    newActivityType === t.type
                      ? 'bg-white text-neutral-900 shadow-sm ring-1 ring-neutral-200'
                      : 'text-neutral-500 hover:bg-white hover:text-neutral-700'
                  }`}
                >
                  <t.icon className={`w-3.5 h-3.5 ${newActivityType === t.type ? 'text-neutral-900' : 'text-neutral-400'}`} />
                  {t.label}
                </button>
              ))}
            </div>
            
            <div className="w-full sm:w-1/2">
                <label className="block text-xs font-medium text-neutral-500 mb-1">Select Fund</label>
                <Select
                    value={selectedFundId}
                    onChange={(e) => {
                        setSelectedFundId(e.target.value);
                        setSelectedContactIds([]); // Reset contacts when fund changes
                    }}
                >
                    <option value="">Choose a fund...</option>
                    {funds.map(f => (
                        <option key={f.id} value={f.id}>{f.name}</option>
                    ))}
                </Select>
            </div>
          </div>
          
          <form onSubmit={handleAddActivity} className="p-4 space-y-4">
            <textarea
              value={newContent}
              onChange={(e) => setNewContent(e.target.value)}
              placeholder={`Describe the ${newActivityType.replace('_', ' ')}...`}
              className="w-full min-h-[100px] p-3 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-200 resize-none"
              autoFocus
            />
            
            <div className="flex items-center justify-between gap-4">
              <div className="w-64">
                <MultiSelect
                  options={selectedFundContacts.map(c => ({
                    label: `${c.name}${c.title ? ` (${c.title})` : ''}`,
                    value: c.id
                  }))}
                  selected={selectedContactIds}
                  onChange={setSelectedContactIds}
                  placeholder={selectedFundId ? "Select contacts..." : "Select a fund first"}
                  className={!selectedFundId ? "opacity-50 pointer-events-none" : ""}
                />
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setIsAdding(false)}
                  text="Cancel"
                />
                <Button
                  type="submit"
                  disabled={!newContent.trim() || !selectedFundId || submitting}
                  text={submitting ? "Saving..." : "Log Activity"}
                />
              </div>
            </div>
          </form>
        </Card>
      )}

      {/* Timeline */}
      <div className="space-y-8 pl-4">
        {loading ? (
          <div className="text-center py-12 text-neutral-400">Loading activities...</div>
        ) : filteredActivities.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="w-6 h-6 text-neutral-400" />
            </div>
            <h3 className="text-neutral-900 font-medium mb-1">No activities found</h3>
            <p className="text-neutral-500 text-sm">
              {searchQuery || typeFilter !== 'all' || fundFilter !== 'all'
                ? "Try adjusting your filters" 
                : "Log your first interaction"}
            </p>
          </div>
        ) : (
          Object.entries(groupedActivities).map(([date, groupActivities]) => (
            <div key={date} className="relative">
              {/* Date Header */}
              <div className="sticky top-20 z-10 -ml-4 mb-4 flex items-center">
                <div className="bg-neutral-100 text-neutral-600 text-xs font-semibold px-3 py-1 rounded-full border border-neutral-200 shadow-sm">
                  {date}
                </div>
              </div>

              {/* Timeline Line */}
              <div className="absolute left-3 top-8 bottom-0 w-px bg-neutral-200" />

              <div className="space-y-6">
                {groupActivities.map((activity) => {
                  const config = getTypeConfig(activity.type);
                  const Icon = config.icon;
                  const displayContacts = activity.contacts?.length 
                    ? activity.contacts 
                    : activity.contact 
                      ? [activity.contact] 
                      : [];

                  return (
                    <div key={activity.id} className="relative pl-10 group">
                      {/* Timeline Dot */}
                      <div className={`absolute left-0 top-3 w-6 h-6 rounded-full border-2 border-white shadow-sm flex items-center justify-center ${config.color} z-10`}>
                        <Icon className="w-3 h-3" />
                      </div>

                      <div className="bg-white border border-neutral-200 rounded-xl p-4 hover:border-neutral-300 hover:shadow-sm transition-all">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                                <span className="font-medium text-sm text-neutral-900 flex items-center gap-2">
                                {config.label}
                                </span>
                                {activity.fund && (
                                    <Link 
                                        to={`/fundraising/funds/${activity.fund.id}`}
                                        className="text-xs text-neutral-500 hover:text-blue-600 hover:underline flex items-center gap-1 bg-neutral-50 px-1.5 py-0.5 rounded border border-neutral-100"
                                    >
                                        <Building2 className="w-3 h-3" />
                                        {activity.fund.name}
                                    </Link>
                                )}
                            </div>
                            
                            {displayContacts.length > 0 && (
                              <div className="flex flex-wrap gap-2">
                                {displayContacts.map((c, i) => (
                                  <span key={i} className="text-xs text-neutral-600 bg-neutral-100 px-1.5 py-0.5 rounded border border-neutral-200">
                                    {c.name} {c.title && <span className="text-neutral-400">({c.title})</span>}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <span className="text-xs text-neutral-400 font-mono flex-shrink-0">
                            {formatTime(activity.createdAt)}
                          </span>
                        </div>
                        
                        <div className="text-sm text-neutral-600 whitespace-pre-wrap leading-relaxed">
                          {activity.content}
                        </div>

                        {/* Metadata Footer */}
                        {activity.type === 'stage_change' && (
                          <div className="mt-3 pt-3 border-t border-neutral-100 flex items-center gap-2">
                            <div className="text-xs text-neutral-500">
                              Moved to <span className="font-medium text-neutral-900">{activity.content.split('to ')?.[1] || 'New Stage'}</span>
                            </div>
                          </div>
                        )}
                        
                        {/* User Attribution */}
                        <div className="mt-3 flex items-center gap-2 text-xs text-neutral-400">
                          <User className="w-3 h-3" />
                          <span>Added by team member</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}





